import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
} from "@material-ui/core";
import {
  Visibility as ViewIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ExpiredIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";
import { useSelector } from "react-redux";
import {
  useLazyObtenerCertificadosPorClienteQuery,
  useLazyGenerarUrlPrefirmadaQuery,
  useEliminarCertificadoMutation,
} from "src/services/certificadosDigitalesApi";
import CertificadoDigitalModal from "./components/CertificadoDigitalModal";
import { useSnackbar } from "src/components/snackbar";
import { AlertaMensajeEliminacion } from "src/components/AlertaMensajeEliminacion";
import Label from "src/components/Label";
import {
  formatFechaCalendario,
  diasRestantesCalendario,
} from "src/utils/formatTime";

const useStyles = makeStyles((theme) => ({
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  tableContainer: {
    maxHeight: 500,
    overflowX: "auto",
  },
  headerCell: {
    fontWeight: "bold",
    backgroundColor: theme.palette.grey[100],
    whiteSpace: "nowrap",
  },
  descripcionCell: {
    maxWidth: 250,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  actionsCell: {
    whiteSpace: "nowrap",
  },
  actionsContainer: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.5),
  },
}));

/**
 * Determina el estado de vigencia del certificado según la fecha de vencimiento
 */
const getEstadoVigencia = (fechaVencimiento) => {
  if (!fechaVencimiento)
    return { label: "Sin fecha", color: "default", icon: null };

  const diffDias = diasRestantesCalendario(fechaVencimiento);

  if (diffDias < 0) {
    return {
      label: "Vencido",
      tipo: "vencido",
      icon: <ExpiredIcon fontSize="small" />,
    };
  }
  if (diffDias <= 30) {
    return {
      label: `Vence en ${diffDias} días`,
      tipo: "porVencer",
      icon: <WarningIcon fontSize="small" />,
    };
  }
  return {
    label: "Vigente",
    tipo: "vigente",
    icon: <CheckCircleIcon fontSize="small" />,
  };
};

const TAB_INDEX = 11;

export default function CertificadosDigitalesCliente({ value, cliente }) {
  const classes = useStyles();
  const { sucursal } = useSelector((state) => state.auth.user);

  const [openModal, setOpenModal] = useState(false);
  const [selectedCertificado, setSelectedCertificado] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [certToDelete, setCertToDelete] = useState(null);

  const { enqueueSnackbar } = useSnackbar();

  const [cargarCertificados, { data: certificados = [], isLoading, error }] =
    useLazyObtenerCertificadosPorClienteQuery();

  const [eliminarCertificado] = useEliminarCertificadoMutation();

  const [generarUrlPrefirmada] = useLazyGenerarUrlPrefirmadaQuery();

  const handleVerArchivo = async (certId) => {
    try {
      const url = await generarUrlPrefirmada({
        id: certId,
        sucursal: sucursal?.refSucursal,
      }).unwrap();
      window.open(url, "_blank");
    } catch (err) {
      enqueueSnackbar("Error al generar enlace del archivo", {
        variant: "error",
      });
    }
  };

  useEffect(() => {
    if (cliente?.rut && value === TAB_INDEX) {
      cargarCertificados({
        rutCliente: cliente.rut,
        sucursal: sucursal?.refSucursal,
      });
    }
  }, [cargarCertificados, cliente?.rut, sucursal?.refSucursal, value]);

  const handleOpenModal = (cert = null) => {
    setSelectedCertificado(cert);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedCertificado(null);
  };

  const handleOpenDeleteDialog = (cert) => {
    setCertToDelete(cert);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCertToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!certToDelete) return;
    try {
      await eliminarCertificado({
        id: certToDelete.id,
        sucursal: sucursal?.refSucursal,
      }).unwrap();
      enqueueSnackbar("Certificado eliminado correctamente", {
        variant: "success",
      });
      handleCloseDeleteDialog();
    } catch (error) {
      enqueueSnackbar(
        error?.data?.message ||
          error?.message ||
          "Error al eliminar certificado",
        { variant: "error" },
      );
    }
  };

  if (value !== TAB_INDEX) return null;

  return (
    <Card>
      <CardContent>
        <div className={classes.headerContainer}>
          <Typography variant="h6">
            Certificados Digitales
            {certificados.length > 0 && (
              <Typography
                variant="caption"
                component="span"
                style={{ marginLeft: 8 }}
              >
                ({certificados.length}{" "}
                {certificados.length === 1 ? "certificado" : "certificados"})
              </Typography>
            )}
          </Typography>

          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
          >
            Nuevo Certificado
          </Button>
        </div>

        {isLoading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        ) : error ? (
          <div className={classes.emptyState}>
            <Typography color="error">
              Error al cargar los certificados:{" "}
              {error?.data?.message || error?.message || "Error desconocido"}
            </Typography>
          </div>
        ) : certificados.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography gutterBottom>
              No hay certificados digitales registrados para este cliente.
            </Typography>
          </div>
        ) : (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headerCell}>Titular</TableCell>
                  <TableCell className={classes.headerCell}>Tipo</TableCell>
                  <TableCell className={classes.headerCell}>Estado</TableCell>
                  <TableCell className={classes.headerCell}>
                    Fecha Emisión
                  </TableCell>
                  <TableCell className={classes.headerCell}>
                    Fecha Vencimiento
                  </TableCell>
                  <TableCell className={classes.headerCell}>Vigencia</TableCell>
                  <TableCell className={classes.headerCell}>
                    Observaciones
                  </TableCell>
                  <TableCell className={classes.headerCell} align="center">
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {certificados.map((cert) => {
                  const vigencia = getEstadoVigencia(cert.fecha_vencimiento);
                  const esActivo = (cert.estado || "ACTIVO") === "ACTIVO";

                  return (
                    <TableRow key={cert.id} hover>
                      <TableCell>{cert.titular || "—"}</TableCell>
                      <TableCell>
                        <Chip label={cert.tipo || "OTRO"} size="small" />
                      </TableCell>
                      <TableCell>
                        <Label
                          variant="ghost"
                          color={esActivo ? "success" : "error"}
                        >
                          {cert.estado || "ACTIVO"}
                        </Label>
                      </TableCell>
                      <TableCell>
                        {formatFechaCalendario(cert.fecha_emision)}
                      </TableCell>
                      <TableCell>
                        {formatFechaCalendario(cert.fecha_vencimiento)}
                      </TableCell>
                      <TableCell>
                        <Label
                          variant="ghost"
                          color={
                            vigencia.tipo === "vigente"
                              ? "success"
                              : vigencia.tipo === "porVencer"
                                ? "warning"
                                : "error"
                          }
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            {vigencia.icon}
                            <span>{vigencia.label}</span>
                          </div>
                        </Label>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={cert.observaciones || ""}>
                          <Typography
                            variant="body2"
                            className={classes.descripcionCell}
                          >
                            {cert.observaciones || "—"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center" className={classes.actionsCell}>
                        <div className={classes.actionsContainer}>
                          {cert.enlace_archivo && (
                            <Tooltip
                              title={
                                cert.nombre_archivo
                                  ? `Ver ${cert.nombre_archivo}`
                                  : "Ver archivo"
                              }
                            >
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleVerArchivo(cert.id)}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Editar certificado">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenModal(cert)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar certificado">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleOpenDeleteDialog(cert)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <CertificadoDigitalModal
          open={openModal}
          onClose={handleCloseModal}
          cliente={cliente}
          certificado={selectedCertificado}
          sucursal={sucursal?.refSucursal}
        />

        <AlertaMensajeEliminacion
          open={deleteDialogOpen}
          setOpen={setDeleteDialogOpen}
          mensaje="¿Estás seguro de que deseas eliminar este certificado digital? Esta acción no se puede deshacer."
          mensajeBotonAceptar="Eliminar"
          aceptar={handleConfirmDelete}
        />
      </CardContent>
    </Card>
  );
}
